import { CHART_TIMEFRAMES } from '@/lib/chart-timeframe'
import { ChartTimeframe } from '@/types/generated/common/types'

export enum PriceScaleMode {
  Normal = 0,
  Logarithmic = 1,
  Percentage = 2,
  IndexedTo100 = 3,
}

/** Candle-interval picker options, coarsest first; `value` is the bar width in seconds. */
export const TIME_FRAME_OPTIONS: { label: string; value: ChartTimeframe }[] = [...CHART_TIMEFRAMES]
  .reverse()
  .map(({ label, timeframe }) => ({ label, value: timeframe }))

/**
 * Quick range presets for the chart toolbar's top row. Unlike TIME_FRAME_OPTIONS
 * (a pure candle-interval picker, still used by IntervalSelector and elsewhere),
 * each preset here changes BOTH the candle interval and the visible time range —
 * e.g. clicking "1y" shows 1d candles over the last 365 days, not just "1d candles".
 */
export interface ChartRangePreset {
  label: string
  /** Candle interval in seconds, same unit as TIME_FRAME_OPTIONS values. */
  timeframeSeconds: number
  /** Visible window length in seconds, ending at "now". */
  rangeSeconds: number
}

const DAY_SECONDS = 60 * 60 * 24

export const CHART_RANGE_PRESETS: ChartRangePreset[] = [
  { label: '5y', timeframeSeconds: DAY_SECONDS * 7, rangeSeconds: DAY_SECONDS * 365 * 5 },
  { label: '1y', timeframeSeconds: DAY_SECONDS, rangeSeconds: DAY_SECONDS * 365 },
  { label: '6m', timeframeSeconds: DAY_SECONDS, rangeSeconds: DAY_SECONDS * 180 },
  { label: '3m', timeframeSeconds: 60 * 60, rangeSeconds: DAY_SECONDS * 30 },
  { label: '1m', timeframeSeconds: 15 * 60, rangeSeconds: DAY_SECONDS * 30 },
  { label: '5d', timeframeSeconds: 5 * 60, rangeSeconds: DAY_SECONDS * 5 },
  { label: '1d', timeframeSeconds: 60, rangeSeconds: 60 * 60 * 25 },
]

export const SCALE_OPTIONS = [
  {
    label: '%',
    value: 'Percentage',
  },
  {
    label: 'Log',
    value: 'Logarithmic',
  },
  {
    label: 'Auto',
    value: 'Normal',
  },
] as const

export type ToolbarPriceScaleMode = (typeof SCALE_OPTIONS)[number]['value']

/**
 * Auto-fit and the Y-axis representation are independent TradingView settings.
 * Auto toggles only pane tracking, while % / Log toggle the scale mode and remain
 * mutually exclusive with each other.
 */
export function toggleChartScaleOption(
  currentScale: keyof typeof PriceScaleMode,
  currentAutoScale: boolean,
  option: ToolbarPriceScaleMode
): { scale: keyof typeof PriceScaleMode; autoScale: boolean } {
  if (option === 'Normal') {
    return { scale: currentScale, autoScale: !currentAutoScale }
  }

  return {
    scale: currentScale === option ? 'Normal' : option,
    autoScale: currentAutoScale,
  }
}

export const getScaleValue = (scale: keyof typeof PriceScaleMode) => {
  return PriceScaleMode[scale]
}

export const CHART_TYPE_OPTIONS = [
  { label: 'Candles', value: 1 },
  { label: 'Bars', value: 0 },
  { label: 'Line', value: 2 },
  { label: 'Area', value: 3 },
  { label: 'Hollow', value: 9 },
  { label: 'Heikin', value: 14 },
] as const
